import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Clock,
  Database,
  BarChart3,
  Smartphone,
  ShieldCheck,
  Layers,
  CheckCircle2,
  ChevronRight,
  Menu,
  X,
  Eye,
  AlertTriangle,
  FileCheck,
  Timer,
  Shield,
  TrendingUp,
  Zap,
  Calendar,
  User,
  Mail,
  Building2,
  MessageSquare,
  Check,
  ChevronLeft,
  Loader2,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/* ─── scroll-reveal hook ─── */
function useReveal(threshold = 0.15) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

const revealClass = (visible) =>
  `transition-all duration-700 ease-out ${
    visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
  }`;

/* ─── tiny reusable ─── */
const Mint = ({ children }) => (
  <span className="text-[#6EE7B7]">{children}</span>
);

const SectionTag = ({ children }) => (
  <span className="inline-block font-mono text-[13px] tracking-[0.12em] uppercase text-[#6EE7B7] mb-5 px-3.5 py-1.5 rounded-full border border-[#6EE7B7]/20 bg-[#6EE7B7]/5">
    {children}
  </span>
);

/* ─── smooth scroll helper ─── */
function scrollTo(id) {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}

/* ─── date helpers ─── */
function addDays(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function formatDateISO(date) {
  return date.toISOString().split("T")[0];
}

function formatDateDisplay(dateStr) {
  const d = new Date(dateStr + "T12:00:00");
  return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
}

/* ─── Navbar ─── */
function Navbar() {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = useCallback((id) => {
    setMobileOpen(false);
    scrollTo(id);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-220 ${
        scrolled
          ? "bg-[#0B0F14]/95 backdrop-blur-md border-b border-[#243041]/80"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 h-[68px] flex items-center justify-between">
        <button onClick={() => scrollTo("hero")} className="flex items-center gap-3 group">
          <img
            src="/assets/amp-mineops-logo.png"
            alt="AMP MineOps"
            className="h-11 w-auto transition-transform duration-200 ease-out group-hover:scale-105"
            style={{
              filter:
                "brightness(0) saturate(100%) invert(82%) sepia(30%) saturate(500%) hue-rotate(100deg) brightness(101%) contrast(92%)",
            }}
          />
        </button>

        <div className="hidden md:flex items-center gap-8">
          {[
            ["problem", "Problem"],
            ["solution", "Solution"],
            ["services", "Services"],
            ["how-it-works", "How It Works"],
            ["engagement", "Engagement"],
          ].map(([id, label]) => (
            <button key={id} onClick={() => handleNav(id)} className="text-[#A7B0BA] hover:text-white text-[13px] font-medium tracking-wide uppercase transition-colors duration-150">
              {label}
            </button>
          ))}
          <button
            onClick={() => navigate("/login")}
            className="text-[#A7B0BA] hover:text-white text-[13px] font-medium tracking-wide uppercase transition-colors duration-150"
          >
            Log In
          </button>
          <button
            onClick={() => handleNav("cta")}
            className="inline-flex items-center gap-2 bg-[#6EE7B7] hover:bg-[#34D399] text-[#0B0F14] font-semibold text-sm px-5 py-2.5 rounded-xl transition-colors duration-150"
          >
            Book a Demo <ArrowRight size={14} />
          </button>
        </div>

        <button
          className="md:hidden text-white"
          onClick={() => setMobileOpen(!mobileOpen)}
        >
          {mobileOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-[#0B0F14]/98 backdrop-blur-md border-t border-[#243041] px-6 pb-6 pt-4 space-y-4">
          {[
            ["problem", "Problem"],
            ["solution", "Solution"],
            ["services", "Services"],
            ["how-it-works", "How It Works"],
            ["engagement", "Engagement"],
          ].map(([id, label]) => (
            <button key={id} onClick={() => handleNav(id)} className="block text-[#A7B0BA] hover:text-white text-sm w-full text-left">{label}</button>
          ))}
          <button onClick={() => { setMobileOpen(false); navigate("/login"); }} className="block text-[#A7B0BA] hover:text-white text-sm w-full text-left">Log In</button>
          <button onClick={() => handleNav("cta")} className="inline-flex items-center gap-2 bg-[#6EE7B7] text-[#0B0F14] font-semibold text-sm px-5 py-2.5 rounded-xl w-full justify-center">
            Book a Demo <ArrowRight size={14} />
          </button>
        </div>
      )}
    </nav>
  );
}

/* ─── Hero ─── */
function Hero() {
  const [ref, visible] = useReveal(0.1);
  return (
    <section id="hero" className="relative min-h-[94vh] flex items-center overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-[#0B0F14] via-[#111824] to-[#0B0F14]" />
      <div className="absolute top-1/4 left-1/3 w-[700px] h-[700px] bg-[#6EE7B7]/[0.03] rounded-full blur-[140px]" />

      <div ref={ref} className="relative max-w-7xl mx-auto px-6 pt-28 pb-20 w-full">
        <div className={`max-w-3xl ${revealClass(visible)}`}>
          <p className="font-mono text-[13px] tracking-[0.15em] uppercase text-[#6EE7B7]/80 mb-6">
            Mine Contractor Intelligence
          </p>

          <h1 className="font-headline font-bold text-[clamp(38px,5.2vw,58px)] leading-[1.04] text-white mb-7">
            Operational truth.{" "}
            <br />
            <Mint>Executive control.</Mint>
          </h1>
          <p className="text-[#A7B0BA] text-lg md:text-[19px] leading-[1.65] max-w-2xl mb-11">
            AMP MineOps turns contractor daily activity into decision-grade
            intelligence — production, delays, utilization, and invoice support
            — without adding headcount.
          </p>

          <div className="flex flex-wrap gap-4 mb-16">
            <button
              onClick={() => scrollTo("cta")}
              className="inline-flex items-center gap-2.5 bg-[#6EE7B7] hover:bg-[#34D399] text-[#0B0F14] font-semibold text-base px-8 py-4 rounded-xl transition-all duration-150 shadow-lg shadow-[#6EE7B7]/10 hover:shadow-[#6EE7B7]/20"
            >
              Book a 20-min Demo <ArrowRight size={16} />
            </button>
            <button
              onClick={() => scrollTo("services")}
              className="inline-flex items-center gap-2.5 border border-[#243041] hover:border-[#A7B0BA]/40 text-white font-medium text-base px-8 py-4 rounded-xl transition-all duration-150"
            >
              Service Scope <ChevronRight size={16} />
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-[640px]">
            {[
              { icon: <Clock size={16} />, text: "Daily reporting in minutes" },
              { icon: <Database size={16} />, text: "Canonical data spine (auditable)" },
              { icon: <BarChart3 size={16} />, text: "Dashboards in Power BI within 24 hrs" },
            ].map((item, i) => (
              <div
                key={i}
                className="flex items-center gap-3 bg-[#1B2430]/50 border border-[#243041]/80 rounded-[14px] px-4 py-3.5"
              >
                <span className="text-[#6EE7B7] flex-shrink-0">{item.icon}</span>
                <span className="text-[13px] text-[#A7B0BA] font-medium leading-snug">
                  {item.text}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Hero visual — dashboard mockup */}
        <div className="hidden lg:block absolute right-6 xl:right-12 top-1/2 -translate-y-1/2 w-[420px] opacity-[0.35]">
          <div className="bg-[#1B2430]/60 border border-[#243041] rounded-2xl p-5 space-y-3">
            <div className="flex items-center justify-between mb-2">
              <div className="flex gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full bg-[#6EE7B7]/40" />
                <div className="w-2.5 h-2.5 rounded-full bg-[#F2C94C]/30" />
                <div className="w-2.5 h-2.5 rounded-full bg-[#A7B0BA]/20" />
              </div>
              <div className="h-2 w-20 bg-[#243041] rounded-full" />
            </div>
            <div className="grid grid-cols-3 gap-2">
              {[
                { label: "Utilization", value: "87.4%", accent: true },
                { label: "Op Hours", value: "1,248" },
                { label: "Delay Hrs", value: "142" },
              ].map((kpi, i) => (
                <div key={i} className={`rounded-xl p-3 border ${kpi.accent ? "border-[#6EE7B7]/25 bg-[#6EE7B7]/[0.06]" : "border-[#243041] bg-[#111824]/60"}`}>
                  <p className="text-[9px] uppercase tracking-wider text-[#A7B0BA]/70 mb-1">{kpi.label}</p>
                  <p className={`font-mono text-sm font-bold ${kpi.accent ? "text-[#6EE7B7]" : "text-white/80"}`}>{kpi.value}</p>
                </div>
              ))}
            </div>
            <div className="flex items-end gap-1.5 h-16 pt-2">
              {[40, 65, 55, 80, 70, 90, 75, 85, 60, 95, 72, 88].map((h, i) => (
                <div
                  key={i}
                  className={`flex-1 rounded-t ${i === 9 ? "bg-[#6EE7B7]/50" : "bg-[#243041]/80"}`}
                  style={{ height: `${h}%` }}
                />
              ))}
            </div>
            <div className="space-y-1.5 pt-1">
              {[1, 2, 3].map((_, i) => (
                <div key={i} className="flex gap-2">
                  <div className="h-2 flex-[2] bg-[#243041]/60 rounded-full" />
                  <div className="h-2 flex-1 bg-[#243041]/40 rounded-full" />
                  <div className="h-2 flex-1 bg-[#243041]/50 rounded-full" />
                </div>
              ))}
            </div>
          </div>
          {/* Logo beneath dashboard with gradient fade effect */}
          <div className="flex justify-center mt-6">
            <div className="relative">
              <img
                src="/assets/amp-mineops-logo.png"
                alt="AMP MineOps"
                className="h-14 w-auto"
                style={{
                  filter:
                    "brightness(0) saturate(100%) invert(82%) sepia(30%) saturate(500%) hue-rotate(100deg) brightness(101%) contrast(92%)",
                  maskImage: "linear-gradient(to bottom, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.15) 100%)",
                  WebkitMaskImage: "linear-gradient(to bottom, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.15) 100%)",
                }}
              />
              {/* Subtle glow effect behind logo */}
              <div 
                className="absolute inset-0 -z-10 blur-xl opacity-40"
                style={{
                  background: "linear-gradient(135deg, #6EE7B7 0%, #34D399 50%, #A7B0BA 100%)",
                  borderRadius: "50%",
                  transform: "scale(1.5)",
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── Credibility strip ─── */
function CredibilityStrip() {
  const [ref, visible] = useReveal(0.2);
  return (
    <section ref={ref} className="border-y border-[#243041]/60 bg-[#111824]/30">
      <div className={`max-w-7xl mx-auto px-6 py-10 ${revealClass(visible)}`}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10">
          {[
            { icon: <Shield size={18} />, label: "Auditable", desc: "Immutable data trail" },
            { icon: <Zap size={18} />, label: "OPEX model", desc: "No capital outlay required" },
            { icon: <TrendingUp size={18} />, label: "90-day ROI", desc: "Measurable from day one" },
            { icon: <Layers size={18} />, label: "Multi-site", desc: "One model, any operation" },
          ].map((item, i) => (
            <div key={i} className="flex items-start gap-3">
              <span className="text-[#6EE7B7] mt-0.5 flex-shrink-0">{item.icon}</span>
              <div>
                <p className="text-white font-semibold text-sm leading-tight">{item.label}</p>
                <p className="text-[#A7B0BA] text-xs mt-0.5">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Problem ─── */
function Problem() {
  const [ref, visible] = useReveal();
  return (
    <section id="problem" className="py-28 relative">
      <div className="absolute inset-0 bg-gradient-to-b from-[#0B0F14] via-[#111824]/50 to-[#0B0F14]" />
      <div ref={ref} className="relative max-w-7xl mx-auto px-6">
        <div className={`max-w-3xl mx-auto text-center ${revealClass(visible)}`}>
          <SectionTag>The Problem</SectionTag>
          <h2 className="font-headline font-bold text-[clamp(24px,3.5vw,34px)] leading-[1.12] text-white mb-6">
            If it isn't structured,{" "}
            <span className="text-[#F2C94C]">it isn't controllable.</span>
          </h2>
          <p className="text-[#A7B0BA] text-lg leading-relaxed max-w-xl mx-auto">
            Contractor logs arrive late, inconsistent, and hard to reconcile —
            and that's where cost leaks learn to hide.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-5 mt-16 max-w-4xl mx-auto">
          {[
            { icon: <AlertTriangle size={20} />, title: "Late & inconsistent logs", desc: "Data arrives in different formats, at different times, from different contractors." },
            { icon: <FileCheck size={20} />, title: "Hard to reconcile", desc: "Manual matching of invoices to activity creates friction and error." },
            { icon: <Timer size={20} />, title: "Hidden cost leaks", desc: "Without structure, underperformance and overcharges go undetected." },
          ].map((card, i) => (
            <div
              key={i}
              className={`bg-[#1B2430] border border-[#243041] rounded-[18px] p-7 hover:border-[#F2C94C]/25 transition-all duration-300 ${revealClass(visible)}`}
              style={{ transitionDelay: `${200 + i * 100}ms` }}
            >
              <span className="text-[#F2C94C] mb-4 block">{card.icon}</span>
              <h3 className="font-headline font-semibold text-white text-[15px] mb-2.5">
                {card.title}
              </h3>
              <p className="text-[#A7B0BA] text-sm leading-relaxed">
                {card.desc}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Solution ─── */
function Solution() {
  const [ref, visible] = useReveal();
  return (
    <section id="solution" className="py-28">
      <div ref={ref} className="max-w-7xl mx-auto px-6">
        <div className={`max-w-3xl mx-auto text-center ${revealClass(visible)}`}>
          <SectionTag>The Solution</SectionTag>
          <h2 className="font-headline font-bold text-[clamp(24px,3.5vw,34px)] leading-[1.12] text-white mb-6">
            One row = one measurable truth.
          </h2>
          <p className="text-[#A7B0BA] text-lg leading-relaxed mb-14 max-w-xl mx-auto">
            Contractors report facts. AMP MineOps standardizes and validates.
            You act fast, with clean numbers.
          </p>
        </div>

        <div className={`flex flex-col md:flex-row items-stretch justify-center gap-4 max-w-4xl mx-auto ${revealClass(visible)}`} style={{ transitionDelay: "200ms" }}>
          {[
            { label: "Contractors", sub: "Report raw activity", detail: "Structured daily forms from any device", color: "border-[#A7B0BA]/20", icon: <Smartphone size={20} /> },
            { label: "AMP MineOps", sub: "Standardize & validate", detail: "Canonical data spine with audit trail", color: "border-[#6EE7B7]/40 bg-[#6EE7B7]/[0.04]", icon: <ShieldCheck size={20} /> },
            { label: "You", sub: "Act on clean data", detail: "Dashboards, KPIs, invoice support", color: "border-[#6EE7B7]/60 bg-[#6EE7B7]/[0.07]", icon: <BarChart3 size={20} /> },
          ].map((step, i) => (
            <React.Fragment key={i}>
              <div className={`flex-1 min-w-[200px] text-center border ${step.color} rounded-[18px] p-7 transition-all`}>
                <span className="text-[#6EE7B7] mb-3 block mx-auto w-fit">{step.icon}</span>
                <p className="font-headline font-semibold text-white text-base mb-1">{step.label}</p>
                <p className="text-[#A7B0BA] text-sm mb-2">{step.sub}</p>
                <p className="text-[#A7B0BA]/60 text-xs">{step.detail}</p>
              </div>
              {i < 2 && (
                <div className="hidden md:flex items-center flex-shrink-0">
                  <ArrowRight size={18} className="text-[#6EE7B7]/50" />
                </div>
              )}
              {i < 2 && (
                <div className="md:hidden flex justify-center py-1">
                  <ArrowRight size={16} className="text-[#6EE7B7]/40 rotate-90" />
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Services ─── */
function Services() {
  const [ref, visible] = useReveal();
  const cards = [
    {
      icon: <Smartphone size={28} />,
      title: "Daily Contractor Reporting",
      desc: "Hours • movement • delay codes • safety/quality notes",
      features: ["Mobile-friendly field input", "Offline save & sync", "Photo attachments"],
    },
    {
      icon: <Database size={28} />,
      title: "Canonical Data Model",
      desc: "Comparable sites, comparable contractors, clean audit trail",
      features: ["Dimension-fact architecture", "Immutable audit log", "Multi-site normalization"],
    },
    {
      icon: <BarChart3 size={28} />,
      title: "Dashboards & KPI Intelligence",
      desc: "Utilization • downtime Pareto • productivity • plan vs actual",
      features: ["Power BI integration", "Real-time KPI chips", "Trend & variance analysis"],
    },
  ];

  return (
    <section id="services" className="py-28 relative">
      <div className="absolute inset-0 bg-gradient-to-b from-[#0B0F14] via-[#111824]/30 to-[#0B0F14]" />
      <div ref={ref} className="relative max-w-7xl mx-auto px-6">
        <div className={`text-center mb-16 ${revealClass(visible)}`}>
          <SectionTag>Core Services</SectionTag>
          <h2 className="font-headline font-bold text-[clamp(24px,3.5vw,34px)] leading-[1.12] text-white mb-4">
            Everything from field to <Mint>boardroom</Mint>
          </h2>
          <p className="text-[#A7B0BA] text-base max-w-lg mx-auto">
            A complete intelligence layer — we handle the data pipeline so you can focus on decisions.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {cards.map((card, i) => (
            <div
              key={i}
              className={`group bg-[#1B2430] border border-[#243041] rounded-[18px] p-8 hover:border-[#6EE7B7]/25 transition-all duration-300 ${revealClass(visible)}`}
              style={{ transitionDelay: `${150 + i * 120}ms` }}
            >
              <span className="text-[#6EE7B7] mb-6 block group-hover:scale-105 transition-transform duration-200 origin-left">
                {card.icon}
              </span>
              <h3 className="font-headline font-semibold text-white text-lg mb-2">
                {card.title}
              </h3>
              <p className="text-[#A7B0BA] text-sm leading-relaxed mb-6">
                {card.desc}
              </p>
              <div className="border-t border-[#243041] pt-5">
                <ul className="space-y-2.5">
                  {card.features.map((f, j) => (
                    <li key={j} className="flex items-center gap-2.5 text-sm text-[#A7B0BA]">
                      <CheckCircle2 size={14} className="text-[#6EE7B7] flex-shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── How It Works ─── */
function HowItWorks() {
  const [ref, visible] = useReveal();
  const steps = [
    { num: "01", icon: <Smartphone size={22} />, title: "Field input (mobile)", desc: "Contractors fill structured daily forms from any device — equipment hours, material movements, delays, safety notes." },
    { num: "02", icon: <ShieldCheck size={22} />, title: "Validation + audit trail", desc: "AMP MineOps runs automatic checks — shift-hour limits, duplicate detection, geofence compliance — and logs every edit." },
    { num: "03", icon: <Layers size={22} />, title: "Canonical truth in Dataverse", desc: "Validated data flows into a dimension-fact model built for cross-site, cross-contractor comparison." },
    { num: "04", icon: <BarChart3 size={22} />, title: "Power BI dashboards for leaders", desc: "Executives see utilization, downtime Pareto, productivity trends, and plan-vs-actual — updated within 24 hours." },
  ];

  return (
    <section id="how-it-works" className="py-28">
      <div ref={ref} className="max-w-7xl mx-auto px-6">
        <div className={`text-center mb-16 ${revealClass(visible)}`}>
          <SectionTag>How It Works</SectionTag>
          <h2 className="font-headline font-bold text-[clamp(24px,3.5vw,34px)] leading-[1.12] text-white">
            From raw field data to <Mint>decision-grade intelligence</Mint>
          </h2>
        </div>

        <div className="grid md:grid-cols-4 gap-6 max-w-5xl mx-auto">
          {steps.map((step, i) => (
            <div
              key={i}
              className={`relative ${revealClass(visible)}`}
              style={{ transitionDelay: `${150 + i * 100}ms` }}
            >
              {i < 3 && (
                <div className="hidden md:block absolute top-11 left-full w-6 h-[2px] bg-gradient-to-r from-[#6EE7B7]/30 to-transparent z-10" />
              )}
              <div className="bg-[#1B2430] border border-[#243041] rounded-[18px] p-6 h-full hover:border-[#6EE7B7]/20 transition-colors duration-200">
                <span className="font-mono text-[#6EE7B7] text-xl font-bold block mb-3">{step.num}</span>
                <span className="text-[#6EE7B7]/80 mb-3 block">{step.icon}</span>
                <h3 className="font-headline font-semibold text-white text-[15px] mb-2">{step.title}</h3>
                <p className="text-[#A7B0BA] text-sm leading-relaxed">{step.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Outcome ─── */
function Outcome() {
  const [ref, visible] = useReveal();
  const outcomes = [
    { icon: <Eye size={18} />, text: "Visibility within 24 hours" },
    { icon: <AlertTriangle size={18} />, text: "Underperformance detected before month-end" },
    { icon: <FileCheck size={18} />, text: "Fewer invoice disputes" },
    { icon: <Timer size={18} />, text: "Downtime gets names, not excuses" },
  ];

  return (
    <section id="outcome" className="py-28 relative">
      <div className="absolute inset-0 bg-gradient-to-b from-[#0B0F14] via-[#111824]/40 to-[#0B0F14]" />
      <div ref={ref} className="relative max-w-7xl mx-auto px-6">
        <div className={`max-w-3xl mx-auto text-center mb-14 ${revealClass(visible)}`}>
          <SectionTag>Results</SectionTag>
          <h2 className="font-headline font-bold text-[clamp(24px,3.5vw,34px)] leading-[1.12] text-white mb-4">
            What changes in the <Mint>first 90 days</Mint>
          </h2>
        </div>

        <div className="grid sm:grid-cols-2 gap-5 max-w-2xl mx-auto">
          {outcomes.map((item, i) => (
            <div
              key={i}
              className={`flex items-start gap-4 bg-[#1B2430] border border-[#243041] rounded-[18px] px-6 py-6 hover:border-[#6EE7B7]/20 transition-all duration-300 ${revealClass(visible)}`}
              style={{ transitionDelay: `${200 + i * 80}ms` }}
            >
              <span className="text-[#6EE7B7] mt-0.5 flex-shrink-0">{item.icon}</span>
              <span className="text-white font-medium text-[15px] leading-snug">{item.text}</span>
            </div>
          ))}
        </div>

        <div className={`flex justify-center mt-12 ${revealClass(visible)}`} style={{ transitionDelay: "500ms" }}>
          <div className="inline-flex items-center gap-4 bg-[#6EE7B7]/[0.07] border border-[#6EE7B7]/15 rounded-2xl px-7 py-4">
            <span className="font-mono text-[#6EE7B7] text-3xl font-bold leading-none">90</span>
            <div className="border-l border-[#6EE7B7]/20 pl-4">
              <span className="text-[#A7B0BA] text-sm leading-snug block">
                days to measurable<br />operational improvement
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── Engagement Model ─── */
function EngagementModel() {
  const [ref, visible] = useReveal();
  return (
    <section id="engagement" className="py-28">
      <div ref={ref} className="max-w-7xl mx-auto px-6">
        <div className={`text-center mb-16 ${revealClass(visible)}`}>
          <SectionTag>Engagement Model</SectionTag>
          <h2 className="font-headline font-bold text-[clamp(24px,3.5vw,34px)] leading-[1.12] text-white mb-4">
            How we <Mint>work together</Mint>
          </h2>
          <p className="text-[#A7B0BA] text-base max-w-lg mx-auto">
            A managed intelligence service — not software you buy, but outcomes you subscribe to.
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          {/* Two-column layout */}
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            {/* OPEX card */}
            <div
              className={`bg-[#1B2430] border border-[#6EE7B7]/20 rounded-[18px] p-8 ${revealClass(visible)}`}
              style={{ transitionDelay: "150ms" }}
            >
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-xl bg-[#6EE7B7]/10 flex items-center justify-center">
                  <Zap size={18} className="text-[#6EE7B7]" />
                </div>
                <div>
                  <h3 className="font-headline font-semibold text-white text-base">OPEX — Not CAPEX</h3>
                  <p className="text-[#A7B0BA] text-xs">Operational expenditure, zero capital outlay</p>
                </div>
              </div>
              <ul className="space-y-3">
                {[
                  "No software licenses to purchase",
                  "No IT infrastructure to deploy",
                  "Predictable monthly cost",
                  "Value visible from month one",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm text-[#A7B0BA]">
                    <Check size={14} className="text-[#6EE7B7] mt-0.5 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Phased card */}
            <div
              className={`bg-[#1B2430] border border-[#243041] rounded-[18px] p-8 ${revealClass(visible)}`}
              style={{ transitionDelay: "250ms" }}
            >
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-xl bg-[#6EE7B7]/10 flex items-center justify-center">
                  <TrendingUp size={18} className="text-[#6EE7B7]" />
                </div>
                <div>
                  <h3 className="font-headline font-semibold text-white text-base">Phased & Measurable</h3>
                  <p className="text-[#A7B0BA] text-xs">Start small, prove value, scale</p>
                </div>
              </div>
              <ul className="space-y-3">
                {[
                  "Pilot with one site or contractor",
                  "Dashboards live within 24 hours of first data",
                  "Scale to multi-site when ready",
                  "Clear KPIs to measure ROI at every stage",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm text-[#A7B0BA]">
                    <Check size={14} className="text-[#6EE7B7] mt-0.5 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* What's included strip */}
          <div
            className={`bg-[#111824]/60 border border-[#243041]/60 rounded-[18px] p-6 md:p-8 ${revealClass(visible)}`}
            style={{ transitionDelay: "350ms" }}
          >
            <p className="font-mono text-[11px] tracking-[0.12em] uppercase text-[#6EE7B7]/70 mb-4">What's included</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                "System setup & configuration",
                "Contractor onboarding support",
                "Data validation & QA",
                "Power BI dashboards",
                "Ongoing support & iteration",
                "Audit-ready data exports",
                "Monthly performance reviews",
                "Dedicated account management",
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-2">
                  <CheckCircle2 size={12} className="text-[#6EE7B7]/60 mt-0.5 flex-shrink-0" />
                  <span className="text-[#A7B0BA] text-xs leading-snug">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── Booking Form ─── */
function BookingForm() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    preferred_date: "",
    preferred_time: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  // Generate available dates (7+ days from now, weekdays only, next 30 days)
  const availableDates = React.useMemo(() => {
    const dates = [];
    const start = addDays(new Date(), 7);
    for (let i = 0; i < 30; i++) {
      const d = addDays(start, i);
      const day = d.getDay();
      if (day !== 0 && day !== 6) {
        dates.push(formatDateISO(d));
      }
    }
    return dates;
  }, []);

  const [calendarWeekStart, setCalendarWeekStart] = useState(0);
  const visibleDates = availableDates.slice(calendarWeekStart, calendarWeekStart + 10);

  const timeSlots = ["9:00 AM", "10:00 AM", "11:00 AM", "2:00 PM", "3:00 PM", "4:00 PM"];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name || !form.email || !form.preferred_date) {
      setError("Please fill in your name, email, and select a date.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/demo-requests`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.detail || "Something went wrong. Please try again.");
      } else {
        setSubmitted(true);
      }
    } catch (err) {
      setError("Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="text-center py-10">
        <div className="w-16 h-16 rounded-full bg-[#6EE7B7]/10 border border-[#6EE7B7]/30 flex items-center justify-center mx-auto mb-5">
          <Check size={28} className="text-[#6EE7B7]" />
        </div>
        <h3 className="font-headline font-bold text-white text-xl mb-3">Demo request received</h3>
        <p className="text-[#A7B0BA] text-base max-w-sm mx-auto mb-2">
          We'll confirm your slot for <span className="text-white font-medium">{formatDateDisplay(form.preferred_date)}</span>
          {form.preferred_time && <> at <span className="text-white font-medium">{form.preferred_time}</span></>} within 24 hours.
        </p>
        <p className="text-[#A7B0BA]/60 text-sm">Check your inbox at {form.email}</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Name + Email row */}
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="text-[#A7B0BA] text-xs font-medium uppercase tracking-wide mb-1.5 block">Full Name *</label>
          <div className="relative">
            <User size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#A7B0BA]/40" />
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="John Smith"
              className="w-full bg-[#111824] border border-[#243041] rounded-xl pl-10 pr-4 py-3 text-sm text-white placeholder:text-[#A7B0BA]/30 focus:outline-none focus:border-[#6EE7B7]/40 transition-colors"
            />
          </div>
        </div>
        <div>
          <label className="text-[#A7B0BA] text-xs font-medium uppercase tracking-wide mb-1.5 block">Work Email *</label>
          <div className="relative">
            <Mail size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#A7B0BA]/40" />
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="john@company.com"
              className="w-full bg-[#111824] border border-[#243041] rounded-xl pl-10 pr-4 py-3 text-sm text-white placeholder:text-[#A7B0BA]/30 focus:outline-none focus:border-[#6EE7B7]/40 transition-colors"
            />
          </div>
        </div>
      </div>

      {/* Company */}
      <div>
        <label className="text-[#A7B0BA] text-xs font-medium uppercase tracking-wide mb-1.5 block">Company</label>
        <div className="relative">
          <Building2 size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#A7B0BA]/40" />
          <input
            type="text"
            value={form.company}
            onChange={(e) => setForm({ ...form, company: e.target.value })}
            placeholder="Company name"
            className="w-full bg-[#111824] border border-[#243041] rounded-xl pl-10 pr-4 py-3 text-sm text-white placeholder:text-[#A7B0BA]/30 focus:outline-none focus:border-[#6EE7B7]/40 transition-colors"
          />
        </div>
      </div>

      {/* Calendar date picker */}
      <div>
        <label className="text-[#A7B0BA] text-xs font-medium uppercase tracking-wide mb-2 flex items-center gap-2 ">
          <Calendar size={13} className="text-[#6EE7B7]/60" /> Select a Date *
        </label>
        <div className="bg-[#111824] border border-[#243041] rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <button
              type="button"
              onClick={() => setCalendarWeekStart(Math.max(0, calendarWeekStart - 5))}
              disabled={calendarWeekStart === 0}
              className="p-1 rounded-lg hover:bg-[#243041] disabled:opacity-20 transition-colors"
            >
              <ChevronLeft size={16} className="text-[#A7B0BA]" />
            </button>
            <span className="text-[#A7B0BA] text-xs font-mono">
              {visibleDates.length > 0 && `${formatDateDisplay(visibleDates[0])} — ${formatDateDisplay(visibleDates[visibleDates.length - 1])}`}
            </span>
            <button
              type="button"
              onClick={() => setCalendarWeekStart(Math.min(availableDates.length - 10, calendarWeekStart + 5))}
              disabled={calendarWeekStart + 10 >= availableDates.length}
              className="p-1 rounded-lg hover:bg-[#243041] disabled:opacity-20 transition-colors"
            >
              <ChevronRight size={16} className="text-[#A7B0BA]" />
            </button>
          </div>
          <div className="grid grid-cols-5 gap-2">
            {visibleDates.map((dateStr) => {
              const d = new Date(dateStr + "T12:00:00");
              const dayName = d.toLocaleDateString("en-US", { weekday: "short" });
              const dayNum = d.getDate();
              const monthName = d.toLocaleDateString("en-US", { month: "short" });
              const selected = form.preferred_date === dateStr;
              return (
                <button
                  key={dateStr}
                  type="button"
                  onClick={() => setForm({ ...form, preferred_date: dateStr })}
                  className={`flex flex-col items-center py-2.5 px-2 rounded-xl border transition-all duration-150 ${
                    selected
                      ? "bg-[#6EE7B7]/10 border-[#6EE7B7]/40 ring-1 ring-[#6EE7B7]/20"
                      : "border-[#243041]/60 hover:border-[#A7B0BA]/30"
                  }`}
                >
                  <span className="text-[10px] uppercase text-[#A7B0BA]/60 font-medium">{dayName}</span>
                  <span className={`font-mono text-lg font-bold leading-tight ${selected ? "text-[#6EE7B7]" : "text-white"}`}>{dayNum}</span>
                  <span className="text-[10px] text-[#A7B0BA]/40">{monthName}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Time slots */}
      <div>
        <label className="text-[#A7B0BA] text-xs font-medium uppercase tracking-wide mb-2 block">Preferred Time (EST)</label>
        <div className="flex flex-wrap gap-2">
          {timeSlots.map((slot) => {
            const selected = form.preferred_time === slot;
            return (
              <button
                key={slot}
                type="button"
                onClick={() => setForm({ ...form, preferred_time: selected ? "" : slot })}
                className={`font-mono text-xs px-4 py-2 rounded-xl border transition-all duration-150 ${
                  selected
                    ? "bg-[#6EE7B7]/10 border-[#6EE7B7]/40 text-[#6EE7B7]"
                    : "border-[#243041] text-[#A7B0BA] hover:border-[#A7B0BA]/30"
                }`}
              >
                {slot}
              </button>
            );
          })}
        </div>
      </div>

      {/* Message */}
      <div>
        <label className="text-[#A7B0BA] text-xs font-medium uppercase tracking-wide mb-1.5 block">Message (optional)</label>
        <div className="relative">
          <MessageSquare size={15} className="absolute left-3.5 top-3.5 text-[#A7B0BA]/40" />
          <textarea
            value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })}
            rows={3}
            placeholder="Tell us about your operation or specific questions..."
            className="w-full bg-[#111824] border border-[#243041] rounded-xl pl-10 pr-4 py-3 text-sm text-white placeholder:text-[#A7B0BA]/30 focus:outline-none focus:border-[#6EE7B7]/40 transition-colors resize-none"
          />
        </div>
      </div>

      {error && (
        <p className="text-red-400 text-sm bg-red-400/10 border border-red-400/20 rounded-xl px-4 py-2.5">{error}</p>
      )}

      <button
        type="submit"
        disabled={submitting}
        className="w-full inline-flex items-center justify-center gap-2.5 bg-[#6EE7B7] hover:bg-[#34D399] disabled:opacity-60 text-[#0B0F14] font-semibold text-base px-8 py-4 rounded-xl transition-all duration-150 shadow-lg shadow-[#6EE7B7]/10"
      >
        {submitting ? (
          <><Loader2 size={18} className="animate-spin" /> Submitting...</>
        ) : (
          <>Request Your Demo <ArrowRight size={16} /></>
        )}
      </button>

      <p className="text-[#A7B0BA]/50 text-xs text-center">
        We'll confirm your slot within 24 hours. Typical demo duration: 20 minutes.
      </p>
    </form>
  );
}

/* ─── CTA ─── */
function CtaSection() {
  const [ref, visible] = useReveal();
  return (
    <section id="cta" className="py-28">
      <div ref={ref} className="max-w-7xl mx-auto px-6">
        <div className={`relative bg-[#1B2430] border border-[#243041] rounded-3xl overflow-hidden ${revealClass(visible)}`}>
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] h-[250px] bg-[#6EE7B7]/[0.04] rounded-full blur-[100px]" />

          <div className="relative grid md:grid-cols-2 gap-0">
            {/* Left — copy */}
            <div className="p-10 md:p-14 flex flex-col justify-center">
              <p className="font-mono text-[13px] tracking-[0.12em] uppercase text-[#6EE7B7]/70 mb-5">
                Ready to see it in action?
              </p>
              <h2 className="font-headline font-bold text-[clamp(24px,3vw,36px)] leading-[1.1] text-white mb-5">
                Stop managing mining <Mint>by rumor.</Mint>
              </h2>
              <p className="text-[#A7B0BA] text-base leading-relaxed mb-8">
                Book a 20-minute personalized walkthrough. We'll show you how AMP MineOps
                turns raw contractor activity into the intelligence your operation needs.
              </p>
              <p className="text-[#A7B0BA]/60 text-sm">
                Or reach us directly:{" "}
                <a href="mailto:ops@ampmineops.com" className="text-[#6EE7B7] hover:text-[#34D399] transition-colors duration-150">
                  ops@ampmineops.com
                </a>
              </p>
            </div>

            {/* Right — booking form */}
            <div className="bg-[#0B0F14]/40 border-t md:border-t-0 md:border-l border-[#243041] p-8 md:p-10">
              <BookingForm />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── Footer ─── */
function Footer() {
  return (
    <footer className="border-t border-[#243041]/60 py-12">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <img
              src="/assets/amp-mineops-logo.png"
              alt="AMP MineOps"
              className="h-9 w-auto"
              style={{
                filter:
                  "brightness(0) saturate(100%) invert(82%) sepia(30%) saturate(500%) hue-rotate(100deg) brightness(101%) contrast(92%)",
              }}
            />
            <span className="text-[#A7B0BA]/70 text-sm">
              &copy; {new Date().getFullYear()} AMP MineOps Intelligence LLC
            </span>
          </div>
          <div className="flex items-center gap-8">
            <a
              href="mailto:ops@ampmineops.com"
              className="text-[#A7B0BA]/70 hover:text-[#6EE7B7] text-sm transition-colors duration-150"
            >
              ops@ampmineops.com
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}

/* ─── Landing Page ─── */
export default function LandingPage() {
  return (
    <div className="bg-[#0B0F14] min-h-screen text-white overflow-x-hidden scroll-smooth">
      <Navbar />
      <Hero />
      <CredibilityStrip />
      <Problem />
      <Solution />
      <Services />
      <HowItWorks />
      <Outcome />
      <EngagementModel />
      <CtaSection />
      <Footer />
    </div>
  );
}
